import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, type WorkReport } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { provinces, getDistricts, parseRegion } from "@/mocks/regionData";
import ImageUploader from "@/components/feature/ImageUploader";

const workTypes = ["칩핑", "파쇄", "상차", "정리", "정비", "기타"];
const weathers = ["맑음", "구름조금", "흐림", "비", "눈", "강풍"];

export default function EditReportPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pageLoading, setPageLoading] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);

  const [formData, setFormData] = useState({
    siteName: "",
    workType: "",
    workDescription: "",
    workerCount: "",
    workHours: "",
    province: "",
    district: "",
    weather: "",
    workDate: "",
    remarks: ""
  });

  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [originalImageUrls, setOriginalImageUrls] = useState<string[]>([]);

  const originalDataRef = useRef({
    siteName: "",
    workType: "",
    workDescription: "",
    workerCount: "",
    workHours: "",
    province: "",
    district: "",
    weather: "",
    workDate: "",
    remarks: ""
  });

  const districts = getDistricts(formData.province);

  const [siteHistory, setSiteHistory] = useState<string[]>([]);
  const [showSiteSuggestions, setShowSiteSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const siteInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const skipBlockRef = useRef(false);
  const pendingNavigationRef = useRef<(() => void) | null>(null);

  const checkDirty = useCallback(() => {
    const original = originalDataRef.current;
    const dirty =
      formData.siteName !== original.siteName ||
      formData.workType !== original.workType ||
      formData.workDescription !== original.workDescription ||
      formData.workerCount !== original.workerCount ||
      formData.workHours !== original.workHours ||
      formData.province !== original.province ||
      formData.district !== original.district ||
      formData.weather !== original.weather ||
      formData.workDate !== original.workDate ||
      formData.remarks !== original.remarks ||
      JSON.stringify(imageUrls) !== JSON.stringify(originalImageUrls);
    setIsDirty(dirty);
  }, [formData, imageUrls, originalImageUrls]);

  useEffect(() => {
    checkDirty();
  }, [checkDirty]);

  // Browser back/forward button interception via popstate
  useEffect(() => {
    if (!isDirty) return;

    // Push a dummy state to intercept browser back
    window.history.pushState(null, "", window.location.href);

    const handlePopState = () => {
      // Push back to prevent actually navigating away
      window.history.pushState(null, "", window.location.href);

      pendingNavigationRef.current = () => {
        skipBlockRef.current = true;
        setIsDirty(false);
        // Remove the dummy state we pushed when entering dirty
        window.history.back();
      };
      setShowBlockModal(true);
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isDirty]);

  // Browser tab close/refresh warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    async function fetchReport() {
      setPageLoading(true);
      try {
        const data = await api.get<WorkReport>(`/reports/${id}`);

        if (data.user_id !== user?.id) {
          navigate(`/report/${id}`);
          return;
        }

        const parsed = parseRegion(data.region);
        const initialData = {
          siteName: data.site_name || "",
          workType: data.work_type || "",
          workDescription: data.work_description || "",
          workerCount: String(data.worker_count || ""),
          workHours: String(data.work_hours || ""),
          province: parsed.province || "",
          district: parsed.district || "",
          weather: data.weather || "",
          workDate: data.work_date || "",
          remarks: data.remarks || ""
        };

        setFormData(initialData);
        originalDataRef.current = { ...initialData };

        const urls = data.image_urls || [];
        setImageUrls(urls);
        setOriginalImageUrls([...urls]);
      } catch {
        navigate(`/report/${id}`);
      } finally {
        setPageLoading(false);
      }
    }

    if (id && user) {
      fetchReport();
    }
  }, [id, user, navigate]);

  useEffect(() => {
    const fetchSiteHistory = async () => {
      try {
        const data = await api.get<{ site_name: string }[]>("/reports");
        const uniqueSites = [...new Set(data.map((r) => r.site_name))];
        setSiteHistory(uniqueSites);
      } catch {
        // silently fail
      }
    };

    fetchSiteHistory();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target as Node) &&
        siteInputRef.current &&
        !siteInputRef.current.contains(e.target as Node)
      ) {
        setShowSiteSuggestions(false);
        setActiveSuggestionIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredSuggestions = siteHistory.filter(
    (site) =>
      site.toLowerCase().includes(formData.siteName.toLowerCase()) &&
      site.toLowerCase() !== formData.siteName.toLowerCase()
  );

  const handleSiteChange = (value: string) => {
    setFormData(prev => ({ ...prev, siteName: value }));
    setShowSiteSuggestions(true);
    setActiveSuggestionIndex(-1);
  };

  const handleSelectSuggestion = (site: string) => {
    setFormData(prev => ({ ...prev, siteName: site }));
    setShowSiteSuggestions(false);
    setActiveSuggestionIndex(-1);
    siteInputRef.current?.focus();
  };

  const handleSiteKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSiteSuggestions || filteredSuggestions.length === 0) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        setShowSiteSuggestions(true);
        setActiveSuggestionIndex(e.key === "ArrowDown" ? 0 : filteredSuggestions.length - 1);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveSuggestionIndex(prev =>
          prev < filteredSuggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveSuggestionIndex(prev =>
          prev > 0 ? prev - 1 : filteredSuggestions.length - 1
        );
        break;
      case "Enter":
        if (activeSuggestionIndex >= 0 && activeSuggestionIndex < filteredSuggestions.length) {
          e.preventDefault();
          handleSelectSuggestion(filteredSuggestions[activeSuggestionIndex]);
        }
        break;
      case "Escape":
        setShowSiteSuggestions(false);
        setActiveSuggestionIndex(-1);
        break;
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => {
      if (field === "province" && prev.province !== value) {
        return { ...prev, [field]: value, district: "" };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !id) return;

    setError("");
    setLoading(true);

    try {
      await api.put(`/reports/${id}`, {
        site_name: formData.siteName,
        work_type: formData.workType,
        work_description: formData.workDescription,
        worker_count: parseInt(formData.workerCount, 10),
        work_hours: parseFloat(formData.workHours),
        region: `${formData.province}${formData.district ? ` ${formData.district}` : ""}`,
        weather: formData.weather,
        work_date: formData.workDate,
        remarks: formData.remarks,
        image_urls: imageUrls,
      });

      setIsDirty(false);
      skipBlockRef.current = true;
      setSaveSuccess(true);
      setTimeout(() => {
        navigate(`/report/${id}`);
      }, 800);
    } catch {
      setError("일보 수정 중 오류가 발생했습니다. 다시 시도해주세요.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      pendingNavigationRef.current = () => {
        skipBlockRef.current = true;
        navigate(`/report/${id}`);
      };
      setShowBlockModal(true);
    } else {
      skipBlockRef.current = true;
      navigate(`/report/${id}`);
    }
  };

  const handleProceed = () => {
    setShowBlockModal(false);
    pendingNavigationRef.current?.();
    pendingNavigationRef.current = null;
  };

  const handleReset = () => {
    setShowBlockModal(false);
    pendingNavigationRef.current = null;
  };

  if (pageLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-gray-400">일보 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      {/* 저장 성공 토스트 */}
      {saveSuccess && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 px-5 py-3 bg-emerald-500 text-white rounded-xl shadow-lg flex items-center gap-2 text-sm font-medium animate-[fadeIn_0.3s_ease-out]">
          <i className="ri-check-line text-lg"></i>
          작업일보가 수정되었습니다
        </div>
      )}

      {/* 변경사항 유실 경고 모달 */}
      {showBlockModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 animate-[fadeIn_0.2s_ease-out]">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <div className="w-12 h-12 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-alert-line text-amber-500 text-xl"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">변경사항이 저장되지 않았습니다</h3>
            <p className="text-sm text-gray-500 text-center mb-6">
              페이지를 떠나면 지금까지 수정한 내용이 모두 사라집니다. 정말 나가시겠습니까?
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={handleReset}
                className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors"
              >
                계속 수정
              </button>
              <button
                onClick={handleProceed}
                className="flex-1 py-2.5 rounded-lg bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 cursor-pointer whitespace-nowrap transition-colors"
              >
                나가기
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6">
        <button onClick={handleCancel} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 cursor-pointer mb-3">
          <i className="ri-arrow-left-line"></i>
          일보 상세
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">작업일보 수정</h1>
            <p className="text-sm text-gray-400 mt-0.5">
              {isDirty ? (
                <span className="flex items-center gap-1.5 text-amber-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                  수정된 내용이 있습니다
                </span>
              ) : (
                "변경된 내용이 없습니다"
              )}
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-5 p-3 rounded-lg bg-red-50 border border-red-100 flex items-center gap-2">
          <i className="ri-error-warning-line text-red-500 text-sm"></i>
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-xl p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                현장명 <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <i className="ri-building-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  ref={siteInputRef}
                  type="text"
                  value={formData.siteName}
                  onChange={(e) => handleSiteChange(e.target.value)}
                  onFocus={() => {
                    if (filteredSuggestions.length > 0 || (siteHistory.length > 0 && formData.siteName === "")) {
                      setShowSiteSuggestions(true);
                    }
                  }}
                  onKeyDown={handleSiteKeyDown}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                  placeholder="현장명을 입력하세요"
                  autoComplete="off"
                  required
                />
                {showSiteSuggestions && (
                  <div
                    ref={suggestionsRef}
                    className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto"
                  >
                    {(filteredSuggestions.length > 0
                      ? filteredSuggestions
                      : siteHistory.filter(s => s.toLowerCase() !== formData.siteName.toLowerCase())
                    ).slice(0, 10).map((site, index) => (
                      <div
                        key={site}
                        onClick={() => handleSelectSuggestion(site)}
                        className={`px-4 py-2.5 cursor-pointer transition-colors flex items-center gap-2.5 text-sm ${
                          index === activeSuggestionIndex
                            ? "bg-amber-50 text-amber-800"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                        onMouseEnter={() => setActiveSuggestionIndex(index)}
                      >
                        <i className="ri-building-line text-gray-400 text-sm"></i>
                        <span>{site}</span>
                      </div>
                    ))}
                    {siteHistory.filter(s => s.toLowerCase() !== formData.siteName.toLowerCase()).length === 0 && (
                      <div className="px-4 py-3 text-sm text-gray-400 text-center">
                        {formData.siteName ? "일치하는 현장명이 없습니다" : "저장된 현장명이 없습니다"}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                작업 유형 <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <i className="ri-tools-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <select
                  value={formData.workType}
                  onChange={(e) => handleChange("workType", e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 appearance-none focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 cursor-pointer"
                  required
                >
                  <option value="">작업 유형 선택</option>
                  {workTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none"></i>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                작업일자 <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <i className="ri-calendar-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="date"
                  value={formData.workDate}
                  onChange={(e) => handleChange("workDate", e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                지역 <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <i className="ri-map-pin-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <select
                  value={formData.province}
                  onChange={(e) => handleChange("province", e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 appearance-none focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 cursor-pointer"
                  required
                >
                  <option value="">시/도 선택</option>
                  {provinces.map((p: string) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none"></i>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                시/군/구 {districts.length > 0 && <span className="text-red-400">*</span>}
              </label>
              <div className="relative">
                <i className="ri-map-pin-2-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <select
                  value={formData.district}
                  onChange={(e) => handleChange("district", e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 appearance-none focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 cursor-pointer"
                  required={districts.length > 0}
                  disabled={districts.length === 0}
                >
                  <option value="">{districts.length === 0 ? "시/도 먼저 선택" : "시/군/구 선택"}</option>
                  {districts.map((d: string) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none"></i>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                날씨 <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <i className="ri-cloud-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <select
                  value={formData.weather}
                  onChange={(e) => handleChange("weather", e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 appearance-none focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 cursor-pointer"
                  required
                >
                  <option value="">날씨 선택</option>
                  {weathers.map(weather => (
                    <option key={weather} value={weather}>{weather}</option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none"></i>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                투입 인원 (명) <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <i className="ri-team-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="number"
                  min="1"
                  value={formData.workerCount}
                  onChange={(e) => handleChange("workerCount", e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                  placeholder="0"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                작업 시간 (시간) <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <i className="ri-time-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="number"
                  min="0.5"
                  step="0.5"
                  value={formData.workHours}
                  onChange={(e) => handleChange("workHours", e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                  placeholder="0"
                  required
                />
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                작업 내용 <span className="text-red-400">*</span>
              </label>
              <textarea
                value={formData.workDescription}
                onChange={(e) => handleChange("workDescription", e.target.value)}
                rows={4}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 resize-none"
                placeholder="작업 내용을 상세히 입력해주세요. (예: 사용 자재, 시공 방법, 작업 구역 등)"
                required
              ></textarea>
            </div>

            <div className="md:col-span-2">
              <ImageUploader imageUrls={imageUrls} onChange={setImageUrls} maxImages={5} />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">특이사항</label>
              <textarea
                value={formData.remarks}
                onChange={(e) => handleChange("remarks", e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 resize-none"
                placeholder="특이사항, 문제점, 건의사항 등을 입력해주세요."
              ></textarea>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={handleCancel}
              className="px-5 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2.5 bg-amber-500 text-white rounded-lg text-sm font-semibold hover:bg-amber-600 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  저장 중...
                </span>
              ) : "수정 완료"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}